import { parseTemplate } from 'url-template';
import formatApiDate from '~/Api/DateTime/formatApiDate';
import mapPagination from '~/Api/Pagination/mapPagination';
import Pagination from '~/Api/Pagination/Pagination';
import ApiClient, { ApiClientErrorResponse } from '~/ApiClient';

import Item from './Item';
import mapProduct, { type RawProduct } from './mapProduct';
import mapProductList from './mapProductList';
import ProductsErrorResponse from './ProductsErrorResponse';
import ProductsStats from './ProductsStats';
import Query from './Query';

import type ProductList from './ProductList';

export enum PricesPresences {
	atLeastOne = 'at-least-one',
}

export enum DatasetPresences {
	present = 'present',
	notPresent = 'not-present',
	all = 'all',
}

export enum FilterParameterName {
	productDatasetPresence = 'productDatasetPresence',
	pricesPresence = 'pricesPresence',
	datasource = 'datasource',
}

export type PricesPresence = 'at-least-one';
export type DatasetPresence = 'present' | 'not-present' | 'all';
export interface Result {
	items: Item[];
	pagination: Pagination;
	stats: ProductsStats;
}

export default function findProducts(client: ApiClient, query: Query, page: number, perPage: number): Promise<Result> {
	const { filterParameters, sortField, order, operator } = query;
	const url = parseTemplate(
		'products{?page,perPage,filter-all,filter-brandName,filter-activeSubstance,filter-atc,filter-mah,filter-country,filter-newerThan,filter-datasource,filter-pricesPresence,filter-productDatasetPresence,filter-operator,dataset-validity,sort,order}'
	);
	const queryParameters: { [key: string]: string | number | null } = {
		page,
		perPage,
		'filter-operator': operator,
		sort: sortField,
		order,
	};

	filterParameters.forEach((filterParameter) => {
		const parsedValue = filterParameter.parseDateValue();
		if (filterParameter.name === 'newerThan' && parsedValue) {
			queryParameters[`filter-${filterParameter.name}`] = formatApiDate(parsedValue);
		} else if (filterParameter.value instanceof Array) {
			queryParameters[`filter-${filterParameter.name}`] = filterParameter.value.join(',');
		} else if (filterParameter.value !== '') {
			queryParameters[`filter-${filterParameter.name}`] = filterParameter.value;
		}
	});

	return client
		.fetch(url.expand(queryParameters))
		.then((response) => response.json())
		.then((response) => ({
			items: response.items.map(
				(item: { product: RawProduct; memberOfLists: ProductList[] }) =>
					new Item(mapProduct(item.product), item.memberOfLists.map(mapProductList))
			),
			pagination: mapPagination(response.pagination),
			stats: new ProductsStats(response.stats.countriesCount),
		}))
		.catch((response) => ApiClientErrorResponse.wrapError(response, ProductsErrorResponse));
}
